import React, { useEffect, useCallback, useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatchedReducer } from 'sb-hooks'
import { intlMessageShape } from 'sb-react-intl'

import UnderlineView from './UnderlineView/UnderlineView'


const fieldReducer = {
  initialState: {
    value: '',
    error: '',
    isValidating: false,
  },
  update: (state, values) => ({ ...state, ...values }),
  setValue: (state, value) => ({ ...state, value }),
  setValidaionStatus: (state, status) => ({ ...state, isValidating: status }),
}


const useReducer = (field) => {
  const modifyInitialState = ({ value, error, ...rest }) => ({
    value: field.value || value,
    error: field.error || error,
    ...rest,
  })

  return useDispatchedReducer(fieldReducer, modifyInitialState)
}

const useFieldSubscribers = ({ ref, field, actions }) => {
  useEffect(() => {
    const handleFieldStartValidate = () => {
      actions.setValidaionStatus(true)
    }

    const handleFieldValidate = (error) => {
      actions.update({
        isValidating: false,
        error,
      })
    }

    const handleChange = async (value) => {
      actions.setValue(value)

      // prevent field from validation until user blurred it once
      if (field.isValidated) {
        await field.debounceValidate()
      }
    }

    const handleUnset = () => {
      actions.setValue(field.value)
      field.debounceValidate()
      ref.current.focus()
    }

    field.on('start validate', handleFieldStartValidate)
    field.on('validate', handleFieldValidate)
    field.on('change', handleChange)
    field.on('unset', handleUnset)

    return () => {
      field.off('start validate', handleFieldStartValidate)
      field.off('validate', handleFieldValidate)
      field.off('change', handleChange)
      field.off('unset', handleUnset)
    }
  }, [])
}

const useHandleChange = ({ field, onChange }) =>
  useCallback((event) => {
    let { value } = event.target
    field.set(value)

    if (typeof onChange === 'function') {
      onChange(event)
    }
  }, [ onChange ])

const useHandleBlur = ({ field, onBlur }) =>
  useCallback(async (event) => {
    await field.debounceValidate()

    // // prevent input from blurring if there is no async validation
    // if (!field.hasAsyncValidators) {
    //   event.preventDefault()
    // }
    //
    // const error = await field.debounceValidate()
    //
    // // blur input if there is no error
    // if (!field.hasAsyncValidators && !error) {
    //   field.node.blur()
    // }

    if (typeof onBlur === 'function') {
      onBlur(event)
    }

  }, [ onBlur ])

const useFocusOnInit = ({ ref, isEnabled }) =>
  useEffect(() => {
    if (isEnabled) {
      ref.current.focus()
    }
  }, [ isEnabled ])

const useFieldRef = ({ field }) => {
  const ref = useRef()

  useEffect(() => {
    field.setRef(ref.current)

    return () => {
      field.unsetRef()
    }
  }, [])

  return ref
}

// TODO: rework this component for real life Select
const SelectField = forwardRef((props, _ref) => {
  const {
    field, type, label, placeholder,
    icon, iconColor,
    errored, loading, readOnly, focusOnInit, withBorder, withEraser, standardView,
    onSubmit, onIconClick, onBlur, onFocus, onChange,
    dataTestId, id, options, autoComplete,
    blackOnFocus, // test for /subscribe-prototype
  } = props

  const [ state, actions ] = useReducer(field)
  const ref = useFieldRef({ field })

  useFieldSubscribers({ ref, field, actions })
  useFocusOnInit({ ref, isEnabled: focusOnInit })

  const handleChange  = useHandleChange({ field, onChange })
  const handleBlur    = useHandleBlur({ field, onBlur })
  const handleErase   = () => field.unset()

  const resolveRef = (node) => {
    ref.current = node

    if (_ref) {
      _ref.current = node
    }
  }

  const nodeProps = {
    id,
    ref: resolveRef,
    type,
    label,
    value: state.value,
    error: state.error,
    errored,
    loading,
    readOnly,
    disabled: readOnly,
    withEraser,
    blackOnFocus, // test for /subscribe-prototype
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus,
    onErase: handleErase,
    dataTestId,
    options,
    autoComplete,
  }

  if (standardView) {
    nodeProps.placeholder = placeholder
    nodeProps.withBorder = withBorder
    nodeProps.onSubmit = onSubmit
  }
  else {
    nodeProps.icon = icon
    nodeProps.iconColor = iconColor
    nodeProps.onIconClick = onIconClick
  }

  return React.createElement(UnderlineView, nodeProps)
})

SelectField.propTypes = {
  ref: PropTypes.shape({
    current: PropTypes.any,
  }),
  field: PropTypes.object.isRequired,
  type: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  errored: PropTypes.bool,
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
  focusOnInit: PropTypes.bool,
  withBorder: PropTypes.bool,
  withEraser: PropTypes.bool,
  onSubmit: PropTypes.func,
  onIconClick: PropTypes.func,
  standardView: PropTypes.bool,
  autoComplete: PropTypes.string,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  options: PropTypes.array,
}

SelectField.defaultProps = {
  // 'nope' was a hack to remove autoComplete, coz 'off' doesn't work
  // bug: https://bugs.chromium.org/p/chromium/issues/detail?id=914451
  // workarond: https://goo.gl/PeZ856
  // 'nope' is ignored? https://github.com/Rendez/downshift/commit/bf876844925545bab3cb32d6ff66c1beeb056199
  autoComplete: 'off',
}


export default SelectField
